import { useTenantData } from "contexts/tenant";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { usePercentageSerialiser } from "hooks/serialisers/percentages";
import { Serialiser } from "hooks/serialisers/types";
import { useMemo } from "react";
import { UnitType } from "src/store/api/generatedApi";

export type MaterialLimitMeasure = "mass" | "small_mass" | "percent";

/** The unit type whose tenant label describes a given measure. */
export const measureUnitType = (measure: MaterialLimitMeasure): UnitType => {
  switch (measure) {
    case "small_mass":
      return "small_mass";
    case "percent":
      return "mass_fraction";
    case "mass":
      return "mass";
  }
};

/**
 * A serialiser that shows a stored mass in the chosen measure and parses edits
 * back to mass, so the working values stay in mass and only the display
 * converts: small mass scales by the tenant factor, percent divides by the
 * default lower charged-mass limit.
 */
export const useMeasureSerialiser = (
  measure: MaterialLimitMeasure,
  chargedMassLower: number | null
): Serialiser<number | null, never> => {
  const { small_mass_per_mass: smallMassPerMass } = useTenantData();
  const mass = useNumberSerialiser({
    min: 0,
    default: { value: null, text: "" },
    decimalPlaces: 3,
  });
  const smallMass = useNumberSerialiser({
    min: 0,
    default: { value: null, text: "" },
    decimalPlaces: 0,
  });
  const percent = usePercentageSerialiser(1);

  return useMemo<Serialiser<number | null, never>>(() => {
    switch (measure) {
      case "small_mass":
        return {
          format: (value) =>
            value == null ? "" : smallMass.format(value * smallMassPerMass),
          parse: (text) => {
            const result = smallMass.parse(text);
            if (!result.valid) {
              return result;
            }
            return {
              valid: true,
              value:
                result.value == null ? null : result.value / smallMassPerMass,
            };
          },
        };
      case "percent":
        return {
          format: (value) =>
            value == null || chargedMassLower == null
              ? ""
              : percent.format(value / chargedMassLower),
          parse: (text) => {
            const result = percent.parse(text);
            if (!result.valid) {
              return result;
            }
            return {
              valid: true,
              value:
                result.value == null || chargedMassLower == null
                  ? null
                  : result.value * chargedMassLower,
            };
          },
        };
      case "mass":
        return {
          format: (value) => mass.format(value),
          parse: (text) => mass.parse(text),
        };
    }
  }, [measure, mass, smallMass, percent, smallMassPerMass, chargedMassLower]);
};
